import styled, { css } from 'styled-components';
import { fadeIn } from '@panel/commons/core/animations';
import { select, withQuery } from '@panel/commons/core/themev2';
import StyledInput from '@panel/commons/lib/Input/StyledInput';
import {
  getGreenColors,
  getMainColor,
  getNeutralColors,
  getOrangeColors,
  getRedColors
} from '@panel/commons/lib/theme';
import { BodyText } from '@panel/commons/lib/typography';

export const Title = styled.h3`
  margin: 15px 0 20px;
  font-family: 'Hind';
  margin-left: 10px;
`;

export const LoaderWrapper = styled.div`
  height: calc(100% + 100px);
  position: absolute;
  top: -100px;
  width: 100%;
`;

export const BannerContainer = styled.div`
  padding: 10px;
  user-select: text;
  caret-color: transparent;
  margin: 20px 20px 20px 10px;
  ${props =>
    props.type === 'error' &&
    css`
      border: 2px solid ${getOrangeColors('base')};
      color: ${getRedColors('base')};
      background-color: ${select('midLightOrange')};
    `}
  ${props =>
    props.type === 'success' &&
    css`
      border: 2px solid ${getGreenColors('base')};
      color: ${getGreenColors('base')};
      background-color: ${select('greenRecommendedBg')};
    `}
  border-radius: 8px;
  font-family: 'Hind';
  font-weight: 500;
  font-size: 16px;
  & h3 {
    font-size: 18px;
  }
  & > div {
    max-width: 100%;
    white-space: break-spaces;
  }
`;

export const FormControl = styled.div`
  display: flex;
  margin-bottom: 30px;
  &.info {
    flex-direction: column;
    margin-right: 20px;
  }
  margin-left: 10px;
`;

export const StyledWrapper = styled.div`
  display: block;
  width: 100%;
  margin-right: 20px;
`;

export const StyledInputWrapper = styled.div`
  position: relative;
  &.search input {
    padding-top: 14px;
  }
  &.search .search-input .wrapper:focus-within {
    box-sizing: border-box;
    border-radius: 8px;
    border: 1px solid ${getMainColor()};
  }
  &.single-company {
    width: 100%;
    padding-right: 20px;
  }
`;

export const TextInput = styled(StyledInput)`
  display: block;
  padding-top: 30px;
  width: 100%;
  &:focus {
    font-size: 14px;
  }
  &.error {
    border-color: ${getRedColors('base')};
  }
`;

export const StyledLabel = styled(BodyText).attrs({ size: 200 })`
  color: ${select('midDarkGray')};
  z-index: 6;
  font-family: ${props => props.theme.v2.fontFamily.default};
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 22px;
  left: 12px;
  position: absolute;
  top: 13px;
  cursor: text;
  letter-spacing: normal;
  word-spacing: normal;
  transition: color 200ms cubic-bezier(0, 0, 0.2, 1) 0ms, transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms;
  animation: 0s ease 0s 1 normal none running none;
  ${({ hasContent }) =>
    hasContent &&
    css`
      font-size: 12px;
      line-height: 16px;
      top: 17px;
      transform: translateY(-10px);
      color: ${select('midDarkGray')};
    `};
  ${StyledInputWrapper}:focus-within &,
  ${StyledInputWrapper}:hover &,
  ${TextInput}:valid ~ &,
  ${TextInput}:disabled ~ & {
    font-size: 12px;
    line-height: 16px;
    top: 17px;
    transform: translateY(-10px);
    color: ${select('midDarkGray')};
  }
  ${StyledInputWrapper}:active &,
  ${StyledInputWrapper}:focus-within &,
  ${TextInput}:active ~ &,
  ${TextInput}:focus ~ & {
    color: ${getOrangeColors('base')};
  }
  &:active {
    color: ${getOrangeColors('base')};
  }
  &.error {
    color: ${getRedColors('base')};
  }
`;

export const InputWrapper = styled.div`
  display: block;
  padding-bottom: 16px;
  position: relative;
  max-width: 810px;
  @media ${({ theme }) => theme.v2.breakpoints.Min.desktop} {
    max-width: 320px;
  }
`;

export const SearchResult = styled.div`
  position: 'absolute';
  z-index: 9;
  display: block;
  text-align: left;
  color: black;
  font-size: 14px;
`;

export const Submit = styled.button`
  padding: 10px 15px;
  border: 1px solid;
  border-radius: 8px;
  margin-top: 20px;
  background: ${select('orange')};
  font-weight: 600;
  color: white;
  font-weight: bold;
  font-family: 'Hind';
  cursor: pointer
  &:hover {
    background: ${select('darkOrangeAux')};
  }
  &:disabled {
    background: ${select('lightOrange')}
    cursor: wait;
  }
`;

export const ErrorMessage = styled.span`
  color: ${getRedColors('base')};
  display: inherit;
  padding: 7px 0px 0px 15px;
  font-size: 12px;
  ${props =>
    props.longText &&
    css`
      width: 220px;
      white-space: break-spaces;
    `}
`;

export const IntegrationInfo = styled.div`
  display: block;
  border: 1px solid ${select('midDarkGray')};
  border-radius: 8px;
  color: ${select('midDarkGray')};
  font-family: 'Hind';
  font-size: 14px;
  white-space: break-spaces;
  padding: 10px 15px;
  user-select: text;
  caret-color: transparent;
  transform-origin: center center;
  opacity: 0;
  animation: ${fadeIn} 0.5s forwards;
  h3 {
    margin-top: 10px;
    font-size: 16px;
    margin-bottom: 10px;
  }
  div.info-container {
    display: flex;
    flex-direction: row;
    ${withQuery.md.down()} {
      flex-direction: column;
    }
  }
  div.info-container div.info-data {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    div {
      user-select: text;
      line-height: 30px;
      overflow-wrap: break-word;
      word-break: break-all;
      white-space: break-spaces;
    }
  }
  div.info-container div.info-action {
    display: flex;
    place-items: center;
    ${withQuery.md.down()} {
      justify-content: flex-end;
    }
  }
  div.info-data .aditional-info {
    display: flex;
    flex-direction: column;
    margin-top: 15px;
    margin-right: 10px;
    margin-bottom: 10px;
    font-size: 14px;
    a {
      color: ${getMainColor()};
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    div.aditional-info-item {
      line-height: 20px;
    }
  }
`;

export const StyledConfirm = styled.div`
  display: flex;
  width: 100%;
  padding: 20px;
`;

export const StyledConfirmBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  font-family: 'Hind';
  padding: 10px;
  .confirm-text {
    padding: 20px;
    text-align: center;
    font-size: 16px;
    font-weight: 400;
    max-width: 300px;
    white-space: break-spaces;
  }
  .btn-container {
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: space-between;
  }
`;

export const StyledConfirmButton = styled.div`
  max-width: 130px;
  width: 100%;
  padding: 10px 15px;
  text-align: center;
  cursor: pointer;
  :hover {
    ${props =>
      props.ok &&
      css`
        background-color: ${select('darkOrange')};
      `}
    ${props =>
      props.cancel &&
      css`
        background-color: ${select('lightGray')};
      `}
  }
  ${props =>
    props.ok &&
    css`
      background-color: ${select('orange')};
      color: ${select('lightGray')};
    `}
  ${props =>
    props.cancel &&
    css`
      background-color: ${select('white')};
      border: 1px solid ${select('midDarkGray')};
      color: ${select('lightBlack')};
    `}

  border-radius: 6px;
`;

export const ToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  max-width: 180px;
  justify-content: flex-start;
  align-items: stretch;
  margin-top: 10px;
  margin-left: 10px;
  margin-right: 10px;
  margin-bottom: 30px;
  height: 48px;
`;

export const ToggleTitle = styled.span`
  font-family: 'Hind';
  font-size: 14px;
  font-weight: 500;
  color: ${select('midDarkGray')};
  margin-left: 10px;
`;

export const ToggleLabel = styled.label`
  font-family: 'Hind';
  font-size: 14px;
  font-weight: 500;
  color: ${select('darkGray')};
  cursor: pointer;
  display: flex;
  flex-grow: 1;
  height: 100%;
  text-align: center;
  justify-content: center;
  align-items: center;
  border: 1px solid ${select('midDarkGray')};
  &:first-of-type {
    border-right: 0px;
    border-radius: 8px 0 0 8px;
    background-color: ${select('lightGray')};
    color: ${select('darkGrey')};
  }
  &:last-of-type {
    border-left: 0px;
    border-radius: 0 8px 8px 0;
    background-color: ${select('lightGray')};
    color: ${select('darkGrey')};
  }
`;

export const ToggleInput = styled.input`
  display: none;
  &:checked + ${ToggleLabel} {
    border: 1px solid ${select('darkOrange')};
    background-color: ${select('orange')};
    color: white;
  }
  &:checked:disabled + ${ToggleLabel} {
    border: 1px solid ${select('midOrange')};
    background-color: ${select('midOrange')};
    color: white;
  }
  &:first-of-type:disabled + ${ToggleLabel}, &:last-of-type:disabled + ${ToggleLabel} {
    color: ${select('midNeutralGray')};
    border-color: ${select('midNeutralGray')};
    cursor: progress;
  }
`;
